export default {
  stops: {
    title: 'Stops',
    pageTitle: 'Stops Management',

    empty: {
      title: 'No stops added yet.',
      description: 'Click New Stop to start creating a new one.',
      createButton: 'New Stop',
    },

    actions: {
      add: 'New Stop',
      edit: 'Edit',
      delete: 'Delete',
      cancel: 'Cancel',
      save: 'Save',
      close: 'Close',
      retry: 'Try again',
      reset: 'Reset',
    },

    search: {
      placeholder: 'Search stops…',
      noResults: 'No results',
      loading: 'Searching…',
      resultCount: '{count} result found | {count} results found',
      showing: 'Showing the first {count}',
      showAll: 'Show all',
      showFirst: 'Show first {count}',
      narrowSearch: 'Narrow your search to see the rest',
    },

    filter: {
      title: 'Filter',
      apply: 'Apply Filters',
      clear: 'Reset',
      tagsLabel: 'Active filters',
      removeTag: 'Remove filter {name}',
      clearAll: 'Clear all filters',
      tags: {
        universal: 'Universal Stops',
        onlyAda: 'Only ADA',
        archived: 'Archived',
      },
      optionsLabel: 'Options',
      categoriesLabel: 'Stop Types',
      modulesLabel: 'Modules',
      campusesLabel: 'Campuses',
      universalLabel: 'Include universal stops',
      onDemandLabel: 'On-Demand only',
      onlyAdaLabel: 'On-Demand only ADA',
      includeArchivedLabel: 'Include archived',
    },

    list: {
      moduleLabel: 'Module',
      categoryLabel: 'Category',
      usedForLabel: 'Used for',
      coverageLabel: 'Coverage',
      campusLabel: 'Campus',
      addressLabel: 'Address',
      pickup: 'Pick-up',
      dropoff: 'Drop-off',
      pickupAndDropoff: 'Pick-up & Drop-off',
      usedIn: 'Used in {count} routes',
      viewMore: 'View {count} more',
      viewLess: 'View less',
      coverageRadius: '{value} {unit} radius',
      coveragePolygon: 'Polygon coverage',
      universalBadge: 'Universal',
      onDemandBadge: 'On-Demand',
      archivedBadge: 'Archived',
    },

    detail: {
      title: 'Stop details',
      generalSection: 'General',
      coverageSection: 'Coverage',
      usageSection: 'Currently in use in the following routes',
      noRoutes: 'This stop is not currently used in any route.',
      wheelchairAccessible: 'Wheelchair accessible',
      moduleHeader: 'Modules',
      pickupHeader: 'Pick-up',
      dropoffHeader: 'Drop-off',
      coordinatesHeader: 'Coordinates',
      polygonAreaHeader: 'Area',
      radiusHeader: 'Radius',
      campusHeader: 'Campus',
      rows: {
        stopName: 'Stop Name',
        modules: 'Modules',
        coverage: 'Coverage',
        category: 'Category',
        responsibleCampus: 'Responsible Campus',
        location: 'Location',
        usedFor: 'Used for',
        wheelchair: 'Wheelchair Accessible',
        currentlyUsedIn: 'Currently used in',
      },
      values: {
        yes: 'Yes',
        no: 'No',
      },
      noLocation: 'No location available for this stop.',
    },

    edit: {
      title: 'Edit stop',
      titleCreate: 'Add New Stop',
      namePlaceholder: 'Enter stop name',
      radiusPlaceholder: 'Radius',
      polygonHint: 'Edit the polygon on the map.',
      selectCategory: 'Select a category',
      selectCampus: 'Select a campus',
      wheelchairToggle: 'Accessible',
      serviceLabel: 'Service',
      onlyAdaToggle: 'On-Demand only ADA',
      onlyAdaCheckbox: 'Only ADA',
      modulesImmutableHint: 'Modules cannot be changed after creation.',
      errorNameRequired: 'Stop name is required.',
      errorLocationRequired: 'Select a location on the map.',
      errorModulesRequired: 'Select at least one module.',
      errorCoverageRequired: 'Choose a coverage type.',
      errorRadiusPositive: 'Enter a radius greater than 0.',
      errorPolygonInvalid: 'Draw a polygon with at least 3 corners.',
      errorCampusRequired: 'Select a responsible campus.',
      coverageCampusOptional: 'Optional for campus stops. Without a coverage, the campus area is used.',
      campusLocationLocked: 'This is a campus stop: its location cannot be changed here. Name, responsible campus and coverage can still be edited.',
    },

    locationUpdate: {
      title: 'Update location',
      body: 'Would you like to update the location of the stop?',
      confirm: 'Yes, update',
      cancel: 'Cancel',
    },

    moduleWarning: {
      title: 'Module change requires confirmation',
      body: 'Changing modules may affect existing routes that use this stop. Are you sure?',
      confirm: 'Yes, change',
      cancel: 'Cancel',
    },

    deleteConfirm: {
      title: 'Delete this stop?',
      body: 'The stop will be archived. Existing routes that reference it will keep their historical link.',
      bodyNamed: 'The stop "{name}" will be archived. Existing routes that reference it will keep their historical link.',
      confirm: 'Yes, delete',
      cancel: 'Cancel',
    },

    deleteBlocked: {
      title: 'Stop cannot be deleted',
      bodyRoutes: 'This stop is currently used by one or more routes. Remove it from the routes below before deleting.',
      bodyRoutesCount: 'The selected stop is currently being used in {count} route. | The selected stop is currently being used in {count} routes.',
      bodyOnDemand: 'The selected stop is currently being used for active requests.',
      bodyCampusActive: 'This stop belongs to an active campus. Deactivate the campus before deleting the stop.',
      viewAll: 'View all',
      viewLess: 'View less',
    },

    errors: {
      STOP_NAME_DUPLICATE_FOR_CAMPUS: 'This name is already used by another campus stop.',
      STOP_PIN_OUTSIDE_POLYGON: 'The stop pin must be inside the coverage polygon.',
      STOP_RADIUS_NEGATIVE: 'Radius must be greater than 0.',
      STOP_POLYGON_INVALID: 'The drawn polygon is invalid. It must have at least 3 distinct corners and must not self-intersect.',
      STOP_CAMPUS_REQUIRED: 'Select a campus for this Campus Stop.',
      STOP_CAMPUS_NOT_AUTHORIZED: 'You are not authorized to assign this campus.',
      CAMPUS_STOP_LOCATION_LOCKED: 'The location of a campus stop cannot be changed here.',
      STOP_FIELD_IMMUTABLE: 'Modules, category, and on-demand cannot be changed after creation.',
      STOP_IN_USE_BY_ROUTES: 'This stop is currently used by one or more routes.',
      STOP_HAS_ACTIVE_ON_DEMAND_REQUESTS: 'The selected stop is currently being used for active requests.',
      STOP_NOT_FOUND: 'Stop not found.',
      saveFailed: 'Failed to save the stop.',
      deleteFailed: 'Failed to delete the stop.',
    },

    legend: {
      title: 'Map legend',
      showAreas: 'Show areas',
      hideAreas: 'Hide areas',
      toggleAll: 'Toggle all',
      searchLocation: 'Search location...',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      // Strings copied verbatim from the VSER-12779 spec / Zeplin (13_Stops_Add4,
      // 14_Stops_Add5, 15_Stops_Add6, AOAwoKB).
      radiusInstruction: 'Click on the map where you want to add a new stop.',
      polygonFromSearchInstruction: 'The stop is added based on your address search. Click to define the corners of the polygon, starting from the stop pin. You can optionally move the pin within the polygon afterward.',
      polygonInstruction: 'Click on the map to set the corners of the polygon defining the coverage area. The stop pin will be added to the first corner by default, but you can move it within the polygon.',
      pinOutsidePolygonWarning: 'The pin must be located within the coverage area.',
      clearArea: 'Clear',
    },

    tabs: {
      shuttleVanpool: 'Shuttle/ Vanpool',
      onDemand: 'On Demand',
    },

    categories: {
      REGULAR: 'Regular',
      PARK_AND_RIDE: 'Park & Ride',
      CAMPUS_STOPS: 'Campus Stops',
      BUS_STOPS: 'Bus Stops',
      DEPOT: 'Depots',
      ON_DEMAND: 'On Demand',
      UNIVERSAL: 'Universal Stops',
    },

    modules: {
      SHUTTLE: 'Shuttle',
      VANPOOL: 'Vanpool',
    },

    coverage: {
      RADIUS: 'Radius',
      POLYGON: 'Polygon',
      none: '—',
    },

    routeStatus: {
      ACTIVE: 'Active',
      DRAFT: 'Draft',
      PASSIVE: 'Passive',
      ARCHIVED: 'Archived',
    },

    feedback: {
      loadError: 'Failed to load stops. Please try again.',
      detailError: 'Failed to load stop details.',
    },
  },
};
